import { useT } from '@/i18n';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { useAISDKChat } from '@assistant-ui/ai-sdk';
import {
  MessagePrimitive,
  ThreadPrimitive,
  useAuiState,
  type FileMessagePartProps,
  type TextMessagePartProps,
} from '@assistant-ui/react';
import { useQuery } from '@tanstack/react-query';
import { Block, Button, Icon, Preloader } from 'framework7-react';
import { useEffect } from 'react';
import Markdown from 'react-markdown';
import { aiKeys, loadThreadMessages } from '../api/assistant-api';
import { ToolRow } from '../tools/ToolCards';
import { Composer } from './Composer';

function MarkdownText({ text }: TextMessagePartProps) {
  return (
    <div className="ai-markdown">
      <Markdown>{text}</Markdown>
    </div>
  );
}

function UserText({ text }: TextMessagePartProps) {
  return <span className="ai-user-text">{text}</span>;
}

function FileChip({ filename }: FileMessagePartProps) {
  const t = useT().aiAssistant;
  return (
    <span className="ai-file-chip">
      <Icon f7="photo" size={14} />
      {filename ?? t.attachment}
    </span>
  );
}

function UserMessage() {
  return (
    <div className="ai-message ai-message-user" data-testid="ai-message-user">
      <div className="ai-bubble ai-bubble-user">
        <MessagePrimitive.Parts components={{ Text: UserText, File: FileChip }} />
      </div>
    </div>
  );
}

function AssistantMessage() {
  return (
    <div className="ai-message ai-message-assistant" data-testid="ai-message-assistant">
      <div className="ai-bubble ai-bubble-assistant">
        <MessagePrimitive.Parts
          components={{ Text: MarkdownText, File: FileChip, tools: { Fallback: ToolRow } }}
        />
      </div>
    </div>
  );
}

function useThreadHistory() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const remoteId = useAuiState((s) => s.threadListItem.remoteId);
  const chat = useAISDKChat();
  const isEmpty = useAuiState((s) => s.thread.messages.length === 0);
  const history = useQuery({
    queryKey: aiKeys.messages(restaurantId, remoteId ?? ''),
    queryFn: () => loadThreadMessages(restaurantId, remoteId ?? ''),
    enabled: !!restaurantId && !!remoteId && isEmpty,
    staleTime: 0,
  });

  const setMessages = chat?.setMessages;
  const loaded = history.data;
  useEffect(() => {
    if (loaded && loaded.length > 0 && setMessages) {
      setMessages((current) => (current.length === 0 ? loaded : current));
    }
  }, [loaded, setMessages]);

  return { history, isEmpty };
}

export function AssistantThread() {
  const t = useT().aiAssistant;
  const { history, isEmpty } = useThreadHistory();

  return (
    <div className="ai-thread">
      <ThreadPrimitive.Viewport className="ai-thread-viewport" data-testid="ai-thread-viewport">
        {isEmpty && history.isFetching && (
          <Block className="ai-thread-state">
            <Preloader />
            <div>{t.loadingConversation}</div>
          </Block>
        )}
        {isEmpty && history.isError && (
          <Block className="ai-thread-state">
            <div>{t.loadHistoryError}</div>
            <Button
              small
              outline
              data-testid="ai-history-retry"
              onClick={() => void history.refetch()}
            >
              {t.retry}
            </Button>
          </Block>
        )}
        {isEmpty && !history.isFetching && !history.isError && (
          <div className="ai-greeting">
            <div className="ai-greeting-title">{t.greetingTitle}</div>
            <div className="ai-greeting-body">{t.greetingBody}</div>
          </div>
        )}
        <ThreadPrimitive.Messages components={{ UserMessage, AssistantMessage }} />
      </ThreadPrimitive.Viewport>
      <Composer />
    </div>
  );
}
